import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import { devWarn, isPuzzleId, type PuzzleId } from '../luna-common';
import '../luna-board-art/luna-board-art';
import '../luna-icon/luna-icon';

type CardLayout = 'horizontal' | 'stacked';

const LAYOUTS: readonly CardLayout[] = ['horizontal', 'stacked'];

@customElement('luna-game-card')
export class LunaGameCard extends LitPage {
  @property({ type: Boolean, attribute: 'hide-play' }) hidePlay = false;

  private _puzzle: PuzzleId = 'map';

  @property({ attribute: 'puzzle' })
  get puzzle(): PuzzleId {
    return this._puzzle;
  }

  set puzzle(value: string | null) {
    const next = String(value ?? '');
    if (!isPuzzleId(next)) {
      devWarn(`luna-game-card: unknown puzzle "${next}" — falling back to "map"`);
      this._puzzle = 'map';
      return;
    }
    this._puzzle = next;
  }

  private _layout: CardLayout = 'horizontal';

  @property({ attribute: 'layout' })
  get layout(): CardLayout {
    return this._layout;
  }

  set layout(value: string | null) {
    const next = String(value ?? '');
    if (!(LAYOUTS as readonly string[]).includes(next)) {
      devWarn(`luna-game-card: unknown layout "${next}" — falling back to "horizontal"`);
      this._layout = 'horizontal';
      return;
    }
    this._layout = next as CardLayout;
  }

  private emitPlay(): void {
    this.dispatchEvent(
      new CustomEvent('luna-play', {
        detail: { puzzle: this._puzzle },
        bubbles: true,
        composed: true,
      })
    );
  }

  private emitHowto(): void {
    this.dispatchEvent(
      new CustomEvent('luna-howto', {
        detail: { puzzle: this._puzzle },
        bubbles: true,
        composed: true,
      })
    );
  }

  content() {
    return html`
      <div
        class="luna-game-card luna-game-card--${this._layout} luna-game-card--${this._puzzle}"
      >
        <div class="luna-game-card__well luna-game-card__well--${this._puzzle}">
          <luna-board-art puzzle=${this._puzzle} size="150"></luna-board-art>
        </div>
        <div class="luna-game-card__body">
          <h3 class="luna-game-card__title mbw-type-title-card"><slot name="title"></slot></h3>
          <p class="luna-game-card__copy mbw-type-body-default"><slot></slot></p>
          <div class="luna-game-card__actions">
            ${this.hidePlay
              ? ''
              : html`<button type="button" class="luna-game-card__play" @click=${this.emitPlay}>
                  <luna-icon name="play" size="18" aria-hidden="true"></luna-icon>
                  <span class="mbw-type-play-label">Play</span>
                </button>`}
            <button type="button" class="luna-game-card__howto" @click=${this.emitHowto}>
              <span class="luna-game-card__howto-label mbw-type-label-chip">How to play</span>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  styles() {
    return css`
      .luna-game-card {
        display: flex;
        background: var(--mbw-surface-card);
        border-radius: var(--luna-radius-card);
        overflow: hidden;
        box-shadow:
          inset 0 0 0 1px var(--mbw-card-hairline),
          0 4px 0 var(--mbw-card-ledge),
          0 10px 22px rgba(20, 40, 90, 0.35); /* pinned: figma Game Card (#14285a) */
      }

      .luna-game-card--horizontal {
        width: 100%;
        height: 214px;
        flex-direction: row;
      }

      .luna-game-card--stacked {
        width: 100%;
        height: 397px;
        flex-direction: column;
      }

      .luna-game-card__well {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        padding: 20px;
      }

      .luna-game-card--horizontal .luna-game-card__well {
        width: 200px;
        height: 214px;
      }

      .luna-game-card--stacked .luna-game-card__well {
        width: 100%;
        height: 190px;
      }

      .luna-game-card__well--map {
        background: var(--mbw-palette-map-tint);
      }

      .luna-game-card__well--pegs {
        background: var(--mbw-palette-pegs-tint);
      }

      .luna-game-card__well--rectangles {
        background: var(--mbw-palette-rectangles-tint);
      }

      .luna-game-card__well--tents {
        background: var(--mbw-palette-tents-tint);
      }

      .luna-game-card__well--unruly {
        background: var(--mbw-palette-unruly-tint);
      }

      .luna-game-card__well--untangle {
        background: var(--mbw-palette-untangle-tint);
      }

      .luna-game-card__body {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 8px;
        box-sizing: border-box;
        padding: 22px;
      }

      .luna-game-card--stacked .luna-game-card__body {
        padding: 20px;
      }

      .luna-game-card__title {
        margin: 0;
        color: var(--cds-text-primary);
      }

      .luna-game-card__copy {
        margin: 0;
        color: var(--mbw-text-body);
      }

      .luna-game-card__actions {
        display: flex;
        gap: 10px;
        margin-top: auto;
      }

      .luna-game-card__play {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 120px;
        height: 56px;
        padding: 0 24px;
        box-sizing: border-box;
        border: none;
        border-radius: var(--luna-radius-btn);
        cursor: pointer;
        box-shadow: inset 0 1px 0 #ffffff, 0 3px 0 rgba(0, 0, 0, 0.15); /* pinned: figma Game Card */
      }

      .luna-game-card__play:active {
        box-shadow: inset 0 1px 0 #ffffff, 0 1px 0 rgba(0, 0, 0, 0.15);
      }

      .luna-game-card__play:focus-visible {
        outline: none;
        box-shadow:
          inset 0 1px 0 #ffffff,
          0 3px 0 rgba(0, 0, 0, 0.15),
          0 0 0 5px #ffffff,
          0 0 0 7px var(--cds-focus);
      }

      .luna-game-card__howto {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 140px;
        height: 56px;
        padding: 0 22px;
        box-sizing: border-box;
        border: none;
        border-radius: var(--luna-radius-btn);
        cursor: pointer;
        background: #eaf1ff; /* pinned: figma Game Card */
        box-shadow: inset 0 0 0 2px var(--luna-outline-checkpoint);
      }

      .luna-game-card__howto:focus-visible {
        outline: none;
        box-shadow:
          inset 0 0 0 2px var(--luna-outline-checkpoint),
          0 0 0 5px #ffffff,
          0 0 0 7px var(--cds-focus);
      }

      .luna-game-card__howto-label {
        color: #0a4bc9; /* pinned: figma Game Card (not a token) */
      }

      /* Per-puzzle Play gradient + label colour (register 6.3). */
      .luna-game-card--map .luna-game-card__play {
        background-image: linear-gradient(180deg, #33835b 0%, #177245 25%, #15693f 100%); /* pinned: figma Game Card */
        color: var(--mbw-palette-map-btn-text);
      }

      .luna-game-card--pegs .luna-game-card__play {
        background-image: linear-gradient(180deg, #ce603e 0%, #c74a24 25%, #b74421 100%); /* pinned: figma Game Card */
        color: var(--mbw-palette-pegs-btn-text);
      }

      .luna-game-card--rectangles .luna-game-card__play {
        background-image: linear-gradient(180deg, #1f79bc 0%, #0067b3 25%, #005fa5 100%); /* pinned: figma Game Card */
        color: var(--mbw-palette-rectangles-btn-text);
      }

      .luna-game-card--tents .luna-game-card__play {
        background-image: linear-gradient(180deg, #b98e1f 0%, #b07f00 25%, #a27500 100%); /* pinned: figma Game Card */
        color: var(--mbw-palette-tents-btn-text);
      }

      .luna-game-card--unruly .luna-game-card__play {
        background-image: linear-gradient(180deg, #a76198 0%, #9b4c8a 25%, #8f467f 100%); /* pinned: figma Game Card */
        color: var(--mbw-palette-unruly-btn-text);
      }

      .luna-game-card--untangle .luna-game-card__play {
        background-image: linear-gradient(180deg, #4f94d9 0%, #3785d4 25%, #337ac3 100%); /* pinned: figma Game Card */
        color: var(--mbw-palette-untangle-btn-text);
      }
    `;
  }
}
